import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateHeySynclyPromptLibraryMutationOptions } from '@/entities/hey-syncly';
import { errorHandler } from '@/error-handler';
import { Dialog } from '@/shared/ui';
import { toast } from '@/stores/pure/ToastStore';

import PromptLibraryCreateDialogBody from './dialog.create-body';
import type { PromptLibraryItem } from './types';

type Props = {
  onExitComplete?: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  workspaceId: string;
  promptLibraryId: string;
  defaultValues: Pick<PromptLibraryItem, 'prompt' | 'title'>;
};

export const PromptLibraryUpdateDialog = ({
  onExitComplete,
  onOpenChange,
  open,
  workspaceId,
  promptLibraryId,
  defaultValues,
}: Props) => {
  const queryClient = useQueryClient();
  const updateMutation = useMutation(
    updateHeySynclyPromptLibraryMutationOptions(queryClient),
  );
  const handleEditPrompt = async ({
    prompt,
    title,
  }: Pick<PromptLibraryItem, 'prompt' | 'title'>) => {
    if (updateMutation.isPending) {
      return;
    }

    try {
      await updateMutation.mutateAsync({
        workspace_id: workspaceId,
        prompt_library_id: promptLibraryId,
        title,
        prompt,
      });

      onOpenChange(false);
      toast('Prompt updated', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to edit prompt'), 'fail');
      throw error;
    }
  };

  return (
    <Dialog
      onExitComplete={onExitComplete}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Dialog.Content size="m">
        <Dialog.Header>
          <Dialog.Title>Edit prompt</Dialog.Title>
          <Dialog.Description className="mt-1 font-normal text-muted-foreground">
            Save prompts you use often. Click to instantly fill the input.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Body className="p-0 m-0 h-157">
          <PromptLibraryCreateDialogBody
            isSubmitting={updateMutation.isPending}
            onCancel={() => onOpenChange(false)}
            onSubmitPrompt={handleEditPrompt}
            defaultValues={defaultValues}
            submitLabel={'Save changes'}
          />
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};
